import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { FronteggProvider, FronteggThemeOptions } from '@frontegg/react';
import { useAppAuth } from './hooks/useAppAuth';
import { ContextHolder } from '@frontegg/rest-api';
import { AuthenticationApi } from '@frontegg/rest-api/auth';
import AuthService from './services/auth.service';

const authService = AuthService.getInstance();

// Monkey-patch Frontegg's token refresh on custom domains with a handoff token.
// Safari ITP blocks the cookie-based refresh call to auth.app.pliable.co.
// Instead of cross-origin calls (also CORS-blocked), we decode the JWT
// and return a synthetic response that satisfies Frontegg's requestAuthorize.
if (authService.handoffToken && authService.isCustomDomain) {
  const handoffJwt = authService.handoffToken;

  // Decode JWT payload (no verification needed — server already verified it)
  function decodeJwtPayload(token: string): any {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch { return {}; }
  }

  AuthenticationApi.prototype.refreshTokenV3 = async function () {
    // Skip the original cookie-based refresh entirely on custom domains.
    // Safari ITP blocks the cookies, and the original may not throw on
    // failure — it can return a response that Frontegg treats as failed
    // auth without entering our catch block. Build response from JWT claims.
    const token = sessionStorage.getItem('plb_handoff_token') || handoffJwt;
    const claims = decodeJwtPayload(token);

    // Check if token is expired
    if (claims.exp && claims.exp * 1000 < Date.now()) {
      throw new Error('Handoff token expired');
    }

    ContextHolder.setAccessToken(token);

    const activeTenant = {
      tenantId: claims.tenantId || '',
      name: claims.tenantId || '',
    };

    return {
      user: {
        sub: claims.sub || '',
        id: claims.sub || '',
        email: claims.email || '',
        name: claims.name || '',
        tenantId: claims.tenantId || '',
        tenantIds: claims.tenantIds || [claims.tenantId],
        roles: claims.roles || [],
        permissions: claims.permissions || [],
        accessToken: token,
        verified: true,
        mfaEnrolled: false,
        activatedForTenant: true,
        tenants: [activeTenant],
        metadata: '',
      },
      tenants: [activeTenant],
      activeTenant,
    } as any;
  };
}

const contextOptions = {
  baseUrl: authService.authUrl,
  clientId: authService.feClientId,
  appId: authService.feAppId,
};

const authOptions = {
  keepSessionAlive: true,
  routes: {
    loginUrl: (authService.useExternalLogin || authService.isCustomDomain) ? '/ext-login' : '/login',
    logoutUrl: '/do/logout',
    authenticatedUrl: '/',
  },
};

const themeOptions: FronteggThemeOptions = {
  loginBox: {},
};

// Module-level guard: once we redirect to sso-bounce, don't do it again in
// this page load. This survives Frontegg re-triggering /oauth/authorize
// multiple times, and unlike cookies/sessionStorage, cannot be cleared by
// Safari's ITP during cross-origin navigations.
let ssoBounceFired = false;

// Custom onRedirectTo that intercepts OAuth authorize attempts
function ssoRedirectInterceptor(path: string, opts?: any) {
  // When we have a handoff token, block ALL Frontegg-initiated redirects.
  // Frontegg will keep trying to redirect because it can't maintain a
  // cookie-based session on custom domains in Safari. We don't need it to —
  // our auth state is managed independently via the handoff JWT.
  if (authService.handoffToken && authService.isCustomDomain) {
    return;
  }

  if (path.includes('/oauth/authorize')) {
    if (authService.isCustomDomain && authService.ssoLoginUrl && !ssoBounceFired && !sessionStorage.getItem('plb_sso_attempted') && !authService.handoffToken) {
      ssoBounceFired = true;
      sessionStorage.setItem('plb_sso_attempted', Date.now().toString());
      const returnTo = encodeURIComponent(window.location.origin);
      const returnToken = encodeURIComponent(authService.returnToken || '');
      const idpUrl = encodeURIComponent(authService.ssoLoginUrl!);
      // Redirect through Flask sso-bounce endpoint which stores returnTo in
      // the server session, then 302s to the IdP.  This avoids localStorage
      // timing issues with React/Frontegg.
      window.location.href = `${authService.authAppUrl}/api/v1/env/sso-bounce?idpUrl=${idpUrl}&returnTo=${returnTo}&returnToken=${returnToken}`;
    } else if (authService.isCustomDomain && !authService.handoffToken) {
      window.location.href = '/ext-login';
    } else if (!authService.isCustomDomain) {
      window.location.href = '/login';
    }
    return;
  }
  // Let all other Frontegg redirects through normally
  if (opts?.refresh) {
    window.location.href = path;
  } else if (opts?.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
}

// Set it before FronteggProvider (catches early redirects)
ContextHolder.setOnRedirectTo(ssoRedirectInterceptor);

// Wrapper component that uses useAuth hook inside FronteggProvider context
function AppContent() {
  const { isLoading, isAuthenticated } = useAppAuth();
  const interceptSet = useRef(false);
  // Re-set our interceptor after FronteggProvider overwrites it
  useEffect(() => {
    if (!interceptSet.current) {
      ContextHolder.setOnRedirectTo(ssoRedirectInterceptor);
      interceptSet.current = true;
    }
  }, []);

  // If this app is configured for external login (local dev), redirect
  // authenticated users to the legacy app immediately before rendering any UI
  if (authService.useExternalLogin && isAuthenticated) {
    window.location.href = authService.authAppUrl;
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return <Outlet />;
}

function App() {
  return (
    <FronteggProvider
      authOptions={authOptions}
      contextOptions={contextOptions}
      themeOptions={themeOptions}
      hostedLoginBox={false}
      entitlementsOptions={{ enabled: true }}
    >
      <AppContent />
    </FronteggProvider>
  );
}

export default App;
